// Agreement V2 signing helpers — drawn signature canvas + PDF generator.
//
// Split out from App.js because (a) App.js is 7000+ lines already, (b) these
// functions are independently testable, (c) PDF generation pulls in jsPDF
// which is heavy and benefits from being lazy-loadable if we ever want to.
//
// Two exports:
//   • <DrawSignature/> — React component, canvas-based pad. Returns a base64
//     data URL via onChange when the user draws. Mode toggle (Type/Draw).
//   • generateSignedPdf(args) — builds the full signed PDF including the
//     PandaDoc-style Signature Certificate page. Returns the PDF as a
//     base64 data URL ready for upload OR download.

import React, { useRef, useEffect, useState } from 'react';
import jsPDF from 'jspdf';

// ─── DrawSignature ───────────────────────────────────────────────────────
// Canvas signature pad with smooth curves (quadratic interp between points).
// Works for mouse, touch, and pen — uses pointer events which unify all three.
// Output: PNG data URL when user lifts pointer. Caller stores it.
//
// Props:
//   value (string)         — current data URL (for re-render after re-mount)
//   typedFallback (string) — what to show typed in handwriting font if no drawing
//   onChange (data => ...) — fires on every stroke-end with current data URL
//                            (null if cleared)
//   height (number)        — pad height in px (default 120)
//   color (string)         — signature ink color (default near-black)
export function DrawSignature({ value, typedFallback, onChange, height = 120, color = '#0C0D0E' }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(!value);

  // Initial render: redraw existing value if present (e.g. user toggles back from Type mode)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Resize for HiDPI screens so strokes aren't blurry. Re-runs on mount only;
    // resize-handler would be nice for window resize but for a small fixed-height
    // pad the cost isn't worth the complexity.
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.2;

    if (value && value.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => { ctx.clearRect(0, 0, rect.width, rect.height); ctx.drawImage(img, 0, 0, rect.width, rect.height); setEmpty(false); };
      img.src = value;
    } else {
      setEmpty(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ptFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = ptFromEvent(e);
    canvasRef.current.setPointerCapture(e.pointerId);
    // Tap-only (no movement) — draw a tiny dot so a single click registers visually.
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(lastRef.current.x, lastRef.current.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    setEmpty(false);
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pt = ptFromEvent(e);
    const ctx = canvasRef.current.getContext('2d');
    // Quadratic curve through the midpoint smooths sharp pixel jitter.
    const midX = (lastRef.current.x + pt.x) / 2;
    const midY = (lastRef.current.y + pt.y) / 2;
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.quadraticCurveTo(lastRef.current.x, lastRef.current.y, midX, midY);
    ctx.stroke();
    lastRef.current = pt;
  };
  const onPointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    // Export at standard DPI so the PNG is reasonable size — toDataURL() respects
    // the canvas's current pixel size. We use canvas.toDataURL() with 'image/png'
    // for lossless. PNG with mostly transparent pixels compresses very well so
    // a 600x120 sig usually lands at 5-12 KB.
    const dataUrl = canvasRef.current.toDataURL('image/png');
    if (onChange) onChange(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height,
          background: '#fff',
          border: '1px dashed #d1d5db',
          borderRadius: 9,
          cursor: 'crosshair',
          touchAction: 'none',
          display: 'block',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      />
      {empty && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
            {typedFallback ? `Sign here, or use the typed signature: ` : 'Draw your signature here'}
            {typedFallback && (
              <span style={{ fontFamily: "'Caveat',cursive,sans-serif", fontSize: 18, color: '#0C0D0E', fontWeight: 700, fontStyle: 'italic', marginLeft: 4 }}>{typedFallback}</span>
            )}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5 }}>
        <button onClick={clear} type="button" style={{ background: 'transparent', border: 'none', color: '#6B7280', fontSize: 11, cursor: 'pointer', padding: '4px 8px' }}>↺ Clear</button>
      </div>
    </div>
  );
}

// ─── PDF generator ───────────────────────────────────────────────────────
// Builds the full signed agreement as a PDF. Three sections:
//   1. Brand cover/header + agreement title
//   2. Full agreement content (text-wrapped, with proper margins)
//   3. Signature section (both parties, drawn images if available else
//      typed name in italic Times)
//   4. Signature Certificate page (PandaDoc style — reference number,
//      both parties' audit fields, signing timestamps, IP)
//
// Returns a base64 data URL (no `data:application/pdf;base64,` prefix —
// just the raw base64 string) ready to be sent to the Drive upload endpoint.
export function generateSignedPdf({ agreement, brandName, brandLogoDataUrl, brandSignerName, brandSignedAt, brandSignerImage, creatorSignerName, creatorSignedAt, creatorSignerImage, creatorSignerEmail, creatorSignerIp, sentAt, agreementRef, content, collabType }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 56;
  const contentW = pageW - margin * 2;

  const fmtFull = (iso) => iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' }) : '—';
  const fmtUTC = (iso) => iso ? new Date(iso).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—';

  // ── Page 1: header + content ──────────────────────────────────────────
  // Logo (top-left, if present + reasonable size)
  let yCursor = margin;
  if (brandLogoDataUrl && brandLogoDataUrl.startsWith('data:image')) {
    try {
      doc.addImage(brandLogoDataUrl, 'PNG', margin, yCursor, 80, 80, undefined, 'FAST');
      yCursor += 90;
    } catch (e) { /* logo load failed — fall through with text */ }
  }
  // Brand name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128); // grey
  doc.text(brandName.toUpperCase(), margin, yCursor);
  yCursor += 14;
  // Document title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(12, 13, 14);
  doc.text(`${collabType} Collaboration Agreement`, margin, yCursor);
  yCursor += 28;

  // Ref number
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(`Document reference: ${agreementRef}`, margin, yCursor);
  yCursor += 22;

  // Content — text-wrap to fit page width
  doc.setFontSize(11);
  doc.setTextColor(31, 41, 55);
  const lineHeight = 16;
  const paragraphs = String(content || '').split('\n');
  for (const p of paragraphs) {
    if (!p.trim()) { yCursor += 8; continue; }
    const wrapped = doc.splitTextToSize(p, contentW);
    for (const line of wrapped) {
      if (yCursor > pageH - margin - 200) {
        // Reserve ~200pt at the bottom of page 1 for the signature block.
        // If content runs over, add a new page for the rest.
        doc.addPage();
        yCursor = margin;
      }
      doc.text(line, margin, yCursor);
      yCursor += lineHeight;
    }
  }

  // ── Signature block ──────────────────────────────────────────────────
  // Force a new page if not enough room left for signatures (~180pt minimum).
  if (yCursor > pageH - margin - 200) {
    doc.addPage();
    yCursor = margin;
  } else {
    yCursor += 24;
  }

  // Horizontal divider
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(margin, yCursor, pageW - margin, yCursor);
  yCursor += 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(107, 114, 128);
  doc.text('SIGNATURES', margin, yCursor);
  yCursor += 18;

  // Two columns: brand left, creator right
  const colW = (contentW - 24) / 2;
  const sigBoxH = 130;
  const renderSigBlock = (x, label, name, image, signedAt, extra) => {
    let y = yCursor;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(label.toUpperCase(), x, y);
    y += 10;
    // Signature box (light background for visual containment)
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(229, 231, 235);
    doc.roundedRect(x, y, colW, 60, 6, 6, 'FD');
    if (image && image.startsWith('data:image')) {
      try {
        doc.addImage(image, 'PNG', x + 8, y + 6, colW - 16, 48, undefined, 'FAST');
      } catch (e) {
        // Image load failed — fall back to typed
        doc.setFont('times', 'italic');
        doc.setFontSize(20);
        doc.setTextColor(12, 13, 14);
        doc.text(name || '', x + 12, y + 38);
      }
    } else {
      // Typed: render in Times italic (closest cross-platform "handwriting" feel)
      doc.setFont('times', 'italic');
      doc.setFontSize(22);
      doc.setTextColor(12, 13, 14);
      doc.text(name || '', x + 12, y + 40);
    }
    y += 66;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(75, 85, 99);
    doc.text(`Name: ${name || '—'}`, x, y);
    y += 12;
    doc.text(`Signed: ${fmtFull(signedAt)}`, x, y);
    if (extra) { y += 12; doc.text(extra, x, y); }
    return y;
  };

  renderSigBlock(margin, 'Signed by brand', brandSignerName, brandSignerImage, brandSignedAt);
  renderSigBlock(margin + colW + 24, 'Signed by creator', creatorSignerName, creatorSignerImage, creatorSignedAt, creatorSignerEmail ? `Email: ${creatorSignerEmail}` : '');
  yCursor += sigBoxH;

  // Footer note on page 1
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text(`Reference: ${agreementRef} · See Signature Certificate (next page) for full audit trail.`, margin, pageH - 28);

  // ── Page 2: Signature Certificate (PandaDoc style) ─────────────────
  doc.addPage();
  let y = margin;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(12, 13, 14);
  doc.text('Signature Certificate', margin, y);
  y += 28;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text(`Reference number: ${agreementRef}`, margin, y);
  y += 18;
  doc.text(`Document completed: ${fmtUTC(creatorSignedAt)}`, margin, y);
  y += 28;

  // Table header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(12, 13, 14);
  const colSigner = margin;
  const colTimestamp = margin + 200;
  const colSig = margin + 380;
  doc.text('Signer', colSigner, y);
  doc.text('Timestamp', colTimestamp, y);
  doc.text('Signature', colSig, y);
  y += 8;
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageW - margin, y);
  y += 12;

  // Brand row
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(31, 41, 55);
  doc.text(`Brand: ${brandName}`, colSigner, y);
  doc.text(`Signed: ${fmtUTC(brandSignedAt)}`, colTimestamp, y);
  // Tiny signature sample at right (image if drawn, name if typed)
  if (brandSignerImage && brandSignerImage.startsWith('data:image')) {
    try { doc.addImage(brandSignerImage, 'PNG', colSig, y - 10, 90, 24, undefined, 'FAST'); } catch (e) { doc.setFont('times', 'italic'); doc.text(brandSignerName, colSig, y); }
  } else {
    doc.setFont('times', 'italic');
    doc.text(brandSignerName || '', colSig, y);
  }
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(`Email: ${agreement.brandSignerEmail || '—'}`, colSigner, y);
  y += 22;

  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  // Creator row
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(31, 41, 55);
  doc.text(`Creator: ${creatorSignerName || '—'}`, colSigner, y);
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Sent: ${fmtUTC(sentAt)}`, colTimestamp, y - 4);
  doc.text(`Signed: ${fmtUTC(creatorSignedAt)}`, colTimestamp, y + 8);
  if (creatorSignerImage && creatorSignerImage.startsWith('data:image')) {
    try { doc.addImage(creatorSignerImage, 'PNG', colSig, y - 10, 90, 24, undefined, 'FAST'); } catch (e) { doc.setFont('times', 'italic'); doc.setFontSize(11); doc.setTextColor(12, 13, 14); doc.text(creatorSignerName || '', colSig, y); }
  } else {
    doc.setFont('times', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(12, 13, 14);
    doc.text(creatorSignerName || '', colSig, y);
  }
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text(`Email: ${creatorSignerEmail || '—'}`, colSigner, y);
  y += 11;
  if (creatorSignerIp) {
    doc.text(`IP address: ${creatorSignerIp}`, colSigner, y);
    y += 11;
  }
  y += 16;

  // Footer
  y = pageH - 60;
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageW - margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text('Signed via kolabs.agency', margin, y);
  doc.text(`Page ${doc.getNumberOfPages()} of ${doc.getNumberOfPages()}`, pageW - margin - 60, y);

  // Return as base64 (without the data URL prefix — caller can add if needed)
  const base64 = doc.output('datauristring').split(',')[1];
  return base64;
}
